/**
 * Y GO Shuttle Services France - Page Entreprises REFONTE
 * Objectif : Parler le langage des entreprises - Démontrer capacité volume + international
 * Ton : B2B, rationnel, orienté performance et fiabilité
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Users, Globe, TrendingUp, FileText, BarChart3, 
  Clock, Shield, Phone, Mail, Calendar, Check, ChevronRight,
  Loader2, Briefcase, Network, DollarSign, CheckCircle2,
  Plane, MapPin, Car, Target, Settings, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header, Footer } from '../components/layout';
import { useCMSPage } from '../hooks/useCMSPage';
import SafeImage from '../components/SafeImage';

const DEFAULT_HERO_IMAGE = 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1600&q=80';

export default function EntreprisesPage() {
  const { content: cmsContent, loading: cmsLoading } = useCMSPage('entreprises');

  // Get hero data from CMS
  const banner = cmsContent?.banner || {};
  const heroImage = banner.image_url || DEFAULT_HERO_IMAGE;
  const heroTitle = banner.title || 'Solutions de transport pour entreprises';
  const heroSubtitle = banner.subtitle || 'Voyages d\'affaires';

  // SEO
  useEffect(() => {
    const meta = cmsContent?.meta || {};
    document.title = meta.title || 'Solutions Transport Entreprises & Business | Y GO Shuttle';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = meta.description || 'Transport VTC entreprise Le Havre : déplacements professionnels, compte business, facturation centralisée. Solution locale, nationale et internationale.';
    
    return () => {
      document.title = 'Y GO Shuttle Services';
    };
  }, [cmsContent]);

  if (cmsLoading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050509]">
      <Header activePage="Entreprises" />
      
      <main className="overflow-x-hidden">
        {/* HEADER COMPACT B2B */}
        <section className="relative min-h-[280px] sm:min-h-[350px] lg:h-[450px] flex items-center overflow-hidden pt-16">
          <div className="absolute inset-0 z-0">
            <SafeImage 
              src={heroImage}
              fallbackSrc={DEFAULT_HERO_IMAGE}
              alt="Solutions entreprises"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, rgba(11,11,11,0.9) 0%, rgba(11,11,11,0.7) 40%, rgba(11,11,11,0) 80%)'
              }}
            />
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl space-y-3 sm:space-y-4">
              <p className="text-[10px] font-medium tracking-[0.35em] uppercase text-[#C9A24D]">
                Solutions B2B • Transport professionnel
              </p>
              <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl lg:text-5xl font-bold text-[#E5E5E5] leading-tight" style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}>
                Solutions de transport pour <span className="text-[#C9A24D]">entreprises</span>
              </h1>
              <p className="text-[#E5E5E5]/70 text-base sm:text-lg">
                Voyages d'affaires & déplacements professionnels
              </p>
              <p className="text-[#E5E5E5]/60 text-sm sm:text-base max-w-2xl">
                Fiabilité opérationnelle • Flexibilité • Maîtrise des coûts
              </p>
              <div className="pt-2 sm:pt-4">
                <Link to="/contact">
                  <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-6 sm:px-8 py-2.5 sm:py-3 rounded-none text-sm sm:text-base">
                    <Mail className="w-4 h-4 mr-2" />
                    Ouvrir un compte entreprise
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1 : VOYAGES D'AFFAIRES */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Voyages d'affaires & <span className="text-[#C9A24D]">déplacements professionnels</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Service dédié aux dirigeants, cadres et collaborateurs en mission
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Cas d'usage professionnels */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6">
                  Cas d'usage professionnels
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      type: 'Déplacements de dirigeants',
                      desc: 'PDG, DG, membres du comité de direction : ponctualité, confidentialité, image professionnelle',
                      icon: Briefcase
                    },
                    {
                      type: 'Missions ponctuelles',
                      desc: 'Cadres en rendez-vous clients, participation à salons, congrès, formations',
                      icon: Target
                    },
                    {
                      type: 'Déplacements inter-sites',
                      desc: 'Navettes entre bureaux, filiales, usines. Fréquence régulière ou ponctuelle',
                      icon: MapPin
                    },
                    {
                      type: 'Rendez-vous clients',
                      desc: 'Accueil de clients ou partenaires. Représentation de l\'entreprise',
                      icon: Users
                    }
                  ].map((cas) => (
                    <div key={cas.type} className="flex items-start gap-4 bg-[#0B0B0B]/40 p-4">
                      <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center flex-shrink-0">
                        <cas.icon className="w-5 h-5 text-[#C9A24D]" />
                      </div>
                      <div>
                        <p className="text-[#F5F5F5] font-semibold text-sm mb-1">{cas.type}</p>
                        <p className="text-[#A7A7A7] text-xs leading-relaxed">{cas.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Standards de service */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6">
                  Standards de service
                </h3>
                <div className="space-y-3">
                  {[
                    'Chauffeurs en costume, présentation irréprochable',
                    'Véhicules premium entretenus (Mercedes Classe E/V, Tesla)',
                    'Ponctualité garantie avec suivi GPS temps réel',
                    'Confidentialité absolue des échanges et trajets',
                    'Assistance dédiée pour comptes entreprises',
                    'Réservation prioritaire même en haute saison',
                    'Facturation mensuelle centralisée',
                    'Contact direct avec responsable de compte'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                      <span className="text-[#D4D4D4] text-sm leading-relaxed">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 : DÉPLACEMENTS FRANCE + INTERNATIONAL */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Déplacements salariés en <span className="text-[#C9A24D]">France & à l'étranger</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Y GO accompagne vos collaborateurs partout dans le monde via notre réseau de partenaires certifiés
              </p>
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-10 h-10 text-[#C9A24D]" />
                    <h3 className="font-['Playfair_Display'] text-2xl font-semibold text-[#F5F5F5]">
                      Capacité internationale
                    </h3>
                  </div>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed mb-6">
                    Grâce à notre réseau de <strong className="text-[#F5F5F5]">partenaires certifiés présents dans 45 pays</strong>, Y GO organise les déplacements de vos collaborateurs <strong>partout dans le monde</strong>. Un seul contact, une seule facturation, des standards de qualité uniformes.
                  </p>
                  <div className="space-y-2">
                    {[
                      'Un seul interlocuteur : votre responsable de compte Y GO',
                      'Facturation centralisée en euros',
                      'Standards de service identiques partout',
                      'Coordination multi-destinations',
                      'Support technique 24/7 en français et anglais'
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-[#C9A24D] text-sm uppercase tracking-wider mb-4">Destinations fréquentes</h4>
                  <div className="space-y-2 mb-6">
                    <div className="bg-[#0B0B0B]/50 p-3">
                      <p className="text-[#F5F5F5] font-semibold text-sm mb-1">🇫🇷 France</p>
                      <p className="text-[#A7A7A7] text-xs">Paris, Lyon, Marseille, Lille, Toulouse, Strasbourg, Nantes, Bordeaux</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3">
                      <p className="text-[#F5F5F5] font-semibold text-sm mb-1">🇪🇺 Europe</p>
                      <p className="text-[#A7A7A7] text-xs">Londres, Bruxelles, Amsterdam, Francfort, Luxembourg, Genève, Milan, Barcelone</p>
                    </div>
                    <div className="bg-[#0B0B0B]/50 p-3">
                      <p className="text-[#F5F5F5] font-semibold text-sm mb-1">🌍 International</p>
                      <p className="text-[#A7A7A7] text-xs">New York, Dubaï, Singapour, Tokyo, Hong Kong (via partenaires)</p>
                    </div>
                  </div>
                  <div className="border-t border-[#C9A24D]/20 pt-4">
                    <p className="text-[#D4D4D4] text-xs italic">
                      <strong className="text-[#C9A24D]">Coordination multi-trajets :</strong> Votre collaborateur en déplacement Paris-Londres-Francfort sur 3 jours ? Un seul contact Y GO planifie et coordonne les 6 trajets (aéroports + inter-sites).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Points de prise en charge */}
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { type: 'Gares', detail: 'Accueil quais, suivi horaires' },
                { type: 'Aéroports', detail: 'Tous terminaux, suivi vols' },
                { type: 'Hôtels d\'affaires', detail: 'Réception ou parking' },
                { type: 'Sites professionnels', detail: 'Bureaux, usines, campus' }
              ].map((point) => (
                <div key={point.type} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-4 text-center backdrop-blur-sm">
                  <MapPin className="w-6 h-6 text-[#C9A24D] mx-auto mb-2" />
                  <p className="text-[#F5F5F5] font-semibold text-sm mb-1">{point.type}</p>
                  <p className="text-[#A7A7A7] text-xs">{point.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3 : GESTION VOLUMES & RÉCURRENCE */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Gestion des <span className="text-[#C9A24D]">volumes</span> & récurrence
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Capacité à gérer des flux importants avec réactivité et continuité de service
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-8">
              {[
                {
                  title: 'Déplacements quotidiens',
                  desc: 'Navettes récurrentes pour vos équipes (domicile-bureau, inter-sites)',
                  icon: Car,
                  exemples: ['Navette gare-siège social', 'Tournées commerciales', 'Rotations inter-bureaux']
                },
                {
                  title: 'Missions longues',
                  desc: 'Support continu pour projets de plusieurs semaines ou mois',
                  icon: Calendar,
                  exemples: ['Chantiers clients', 'Projets délocalisés', 'Détachements temporaires']
                },
                {
                  title: 'Pics d\'activité',
                  desc: 'Capacité à absorber des volumes exceptionnels lors d\'événements',
                  icon: TrendingUp,
                  exemples: ['Salons professionnels', 'Assemblées générales', 'Séminaires annuels']
                }
              ].map((cas) => (
                <div key={cas.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <cas.icon className="w-8 h-8 text-[#C9A24D] mb-4" />
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-2">
                    {cas.title}
                  </h3>
                  <p className="text-[#A7A7A7] text-sm mb-4 leading-relaxed">{cas.desc}</p>
                  <div className="space-y-1">
                    {cas.exemples.map((ex, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-[#C9A24D] rounded-full" />
                        <span className="text-[#D4D4D4] text-xs">{ex}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Avantages opérationnels */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5] mb-6 text-center">
                Nos capacités opérationnelles
              </h3>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { title: 'Planification', desc: 'Courses programmées jusqu\'à 6 mois à l\'avance', icon: Calendar },
                  { title: 'Suivi en temps réel', desc: 'Tableau de bord entreprise avec localisation véhicules', icon: MapPin },
                  { title: 'Réactivité', desc: 'Réservation d\'urgence possible jusqu\'à 2h avant', icon: Clock },
                  { title: 'Continuité', desc: 'Flotte de secours en cas d\'imprévu technique', icon: Shield }
                ].map((avantage) => (
                  <div key={avantage.title} className="text-center">
                    <avantage.icon className="w-8 h-8 text-[#C9A24D] mx-auto mb-3" />
                    <h4 className="text-[#F5F5F5] font-semibold text-sm mb-2">{avantage.title}</h4>
                    <p className="text-[#A7A7A7] text-xs leading-relaxed">{avantage.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 : AVANTAGES TARIFAIRES VOLUME */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Avantages tarifaires selon <span className="text-[#C9A24D]">volume</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Plus vous utilisez nos services, plus vos conditions sont avantageuses
              </p>
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm mb-8">
              <div className="flex items-start gap-4 mb-6">
                <DollarSign className="w-10 h-10 text-[#C9A24D] flex-shrink-0" />
                <div>
                  <h3 className="text-[#F5F5F5] text-lg font-semibold mb-2">
                    Principe de tarification volume
                  </h3>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    Y GO adapte ses tarifs en fonction du <strong className="text-[#F5F5F5]">volume mensuel de courses</strong> et de la <strong className="text-[#F5F5F5]">durée d'engagement</strong>. Plus votre entreprise utilise nos services régulièrement, plus les <strong className="text-[#C9A24D]">conditions tarifaires deviennent avantageuses</strong>. Un devis personnalisé vous est proposé après étude de vos besoins.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    volume: 'Volume Standard',
                    detail: '1 à 10 courses/mois',
                    avantages: [
                      'Facturation mensuelle centralisée',
                      'Reporting mensuel des courses',
                      'Contact prioritaire'
                    ]
                  },
                  {
                    volume: 'Volume Intermédiaire',
                    detail: '10 à 30 courses/mois',
                    avantages: [
                      'Conditions tarifaires préférentielles',
                      'Responsable de compte dédié',
                      'Dashboard entreprise en ligne',
                      'Délai de paiement adapté'
                    ]
                  },
                  {
                    volume: 'Volume Important',
                    detail: '30+ courses/mois',
                    avantages: [
                      'Tarifs négociés sur mesure',
                      'SLA personnalisé',
                      'Véhicules dédiés si besoin',
                      'Reporting personnalisé',
                      'Audit annuel offert'
                    ]
                  }
                ].map((tier) => (
                  <div key={tier.volume} className="bg-[#0B0B0B]/50 p-6 border border-[#C9A24D]/20">
                    <h4 className="text-[#C9A24D] font-bold mb-1">{tier.volume}</h4>
                    <p className="text-[#A7A7A7] text-xs mb-4">{tier.detail}</p>
                    <div className="space-y-2">
                      {tier.avantages.map((av, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Check className="w-3 h-3 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                          <span className="text-[#D4D4D4] text-xs">{av}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <p className="text-[#A7A7A7] text-sm mb-4">
                Les tarifs sont établis après étude de vos besoins réels (fréquence, distances, type de véhicules)
              </p>
              <Link to="/contact">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-6 sm:px-8 py-3 rounded-none text-sm sm:text-base whitespace-normal">
                  <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">Demander une étude tarifaire personnalisée</span>
                  <span className="sm:hidden">Demander un devis</span>
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* SECTION 5 : PILOTAGE & FACTURATION */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Pilotage, suivi & <span className="text-[#C9A24D]">facturation</span>
              </h2>
              <p className="text-[#A7A7A7] text-base max-w-3xl mx-auto">
                Maîtrise administrative et simplification comptable pour vos équipes
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Facturation */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                    Facturation centralisée
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    'Une seule facture mensuelle pour toutes les courses',
                    'Détail par course : date, trajet, passager, montant',
                    'Export comptable (CSV, Excel)',
                    'Ventilation par service ou centre de coût (sur demande)',
                    'Délais de paiement adaptés (30-45 jours)',
                    'Paiement par virement ou prélèvement'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                      <span className="text-[#D4D4D4] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Suivi & Reporting */}
              <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#C9A24D]/12 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-[#C9A24D]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F5]">
                    Suivi & reporting
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    'Dashboard entreprise en ligne (comptes >10 courses/mois)',
                    'Historique complet des courses',
                    'Statistiques : volume, dépenses, destinations',
                    'Export de données pour contrôle de gestion',
                    'Alertes en cas de retard ou incident',
                    'Rapport mensuel d\'activité (sur demande)'
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                      <span className="text-[#D4D4D4] text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Simplification comptable */}
            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm border-l-4 border-[#C9A24D]">
              <div className="flex items-start gap-4">
                <Settings className="w-6 h-6 text-[#C9A24D] flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-[#F5F5F5] font-semibold mb-2">Simplification comptable</h4>
                  <p className="text-[#D4D4D4] text-sm leading-relaxed">
                    <strong>Un seul fournisseur, une seule facture mensuelle</strong> pour tous vos déplacements (locaux, nationaux, internationaux via réseau). Vos équipes comptables n'ont plus à gérer des dizaines de notes de frais ou de factures multiples. <strong className="text-[#C9A24D]">Gain de temps administratif considérable.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 : POURQUOI Y GO POUR VOTRE ENTREPRISE */}
        <section className="py-20 bg-[#0B0B0B] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Pourquoi <span className="text-[#C9A24D]">Y GO</span> pour votre entreprise ?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Fiabilité opérationnelle',
                  icon: Shield,
                  points: [
                    'Taux de ponctualité > 98%',
                    'Véhicules de secours disponibles',
                    'Double vérification avant chaque course',
                    'Assistance technique 24/7'
                  ]
                },
                {
                  title: 'Couverture étendue',
                  icon: Globe,
                  points: [
                    'Services directs en Normandie',
                    'Réseau national (toute la France)',
                    'Réseau international (45 pays)',
                    'Un seul interlocuteur partout'
                  ]
                },
                {
                  title: 'Qualité homogène',
                  icon: CheckCircle2,
                  points: [
                    'Mêmes standards partout',
                    'Partenaires certifiés uniquement',
                    'Véhicules premium (<3 ans)',
                    'Chauffeurs formés'
                  ]
                },
                {
                  title: 'Flexibilité',
                  icon: Settings,
                  points: [
                    'Modification jusqu\'à 12h avant',
                    'Annulation gratuite 24h',
                    'Options personnalisables',
                    'Contrat adaptable'
                  ]
                },
                {
                  title: 'Interlocuteur unique',
                  icon: Users,
                  points: [
                    'Responsable de compte dédié',
                    'Numéro direct',
                    'Email prioritaire',
                    'Suivi proactif'
                  ]
                },
                {
                  title: 'Maîtrise budgétaire',
                  icon: BarChart3,
                  points: [
                    'Tarifs négociés selon volume',
                    'Facturation mensuelle prévisible',
                    'Reporting budgétaire',
                    'Aucun frais caché'
                  ]
                }
              ].map((reason) => (
                <div key={reason.title} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#C9A24D]/12 flex items-center justify-center">
                      <reason.icon className="w-5 h-5 text-[#C9A24D]" />
                    </div>
                    <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5]">
                      {reason.title}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {reason.points.map((point, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check className="w-3 h-3 text-[#C9A24D] flex-shrink-0 mt-0.5" />
                        <span className="text-[#D4D4D4] text-xs leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* COMMENT DÉMARRER */}
        <section className="py-20 bg-[#050509] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
                Comment <span className="text-[#C9A24D]">démarrer</span> ?
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {[
                { num: '1', title: 'Contact initial', desc: 'Échangez avec notre équipe B2B pour présenter vos besoins' },
                { num: '2', title: 'Étude personnalisée', desc: 'Nous analysons vos flux et proposons une solution sur mesure sous 48h' },
                { num: '3', title: 'Mise en service', desc: 'Ouverture compte, briefing équipes, démarrage immédiat' }
              ].map((step) => (
                <div key={step.num} className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 text-center backdrop-blur-sm">
                  <div className="w-12 h-12 bg-[#C9A24D] flex items-center justify-center mx-auto mb-4 text-[#050509] font-bold text-xl">
                    {step.num}
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#F5F5F5] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-[#A7A7A7] text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] p-6 backdrop-blur-sm text-center">
              <p className="text-[#D4D4D4] text-sm mb-4">
                <strong className="text-[#F5F5F5]">Aucun engagement minimum.</strong> Testez nos services avec une ou deux courses avant de décider. La qualité parle d'elle-même.
              </p>
            </div>
          </div>
        </section>

        {/* CTA FINAL B2B SOBRE */}
        <section className="py-20 bg-gradient-to-r from-[#111111] to-[#1F1F1F] border-t border-[#C9A24D]/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#F5F5F5] mb-4">
              Étudions ensemble votre <span className="text-[#C9A24D]">solution transport</span>
            </h2>
            <p className="text-[#A7A7A7] text-base mb-8 max-w-2xl mx-auto">
              Échangeons sur vos besoins pour construire une offre adaptée à votre entreprise
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
              <Link to="/contact">
                <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] font-semibold px-6 sm:px-10 py-4 rounded-none text-sm sm:text-base w-full sm:w-auto">
                  <Mail className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">Demander une solution entreprise</span>
                  <span className="sm:hidden">Solution entreprise</span>
                </Button>
              </Link>
              <a href="tel:+33278780404">
                <Button
                  variant="outline"
                  className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10 px-6 sm:px-10 py-4 rounded-none text-sm sm:text-base w-full sm:w-auto"
                >
                  <Phone className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">Parler à notre équipe B2B</span>
                  <span className="sm:hidden">Appeler</span>
                </Button>
              </a>
            </div>

            {/* Contact B2B */}
            <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <a 
                href="tel:+33278780404"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Phone className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  Service B2B dédié
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Lundi-Vendredi 9h-18h</p>
              </a>
              <a 
                href="mailto:business@y-go.fr"
                className="relative overflow-hidden bg-[linear-gradient(135deg,rgba(201,162,77,0.16),rgba(10,8,5,0.96))] shadow-[0_18px_45px_rgba(0,0,0,0.65)] hover:shadow-[0_22px_55px_rgba(0,0,0,0.85)] transition-all p-6 text-center backdrop-blur-sm group"
              >
                <Mail className="w-6 h-6 text-[#C9A24D] mx-auto mb-3" />
                <p className="text-[#E5E5E5] font-medium group-hover:text-[#C9A24D] transition-colors">
                  business@y-go.fr
                </p>
                <p className="text-[#A7A7A7] text-xs mt-1">Réponse sous 4h ouvrées</p>
              </a>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
